import {
  Check,
  DataType,
  Error,
  Orientation,
  SIconType,
  Space,
} from "../../types";
import { Colors, Metrics, Styles, Typography } from "../../resources";
import { Pressable, StyleSheet, Text, View } from "react-native";
import React, { useRef, useState } from "react";
import { SIcon, SIconLink, SPercentChip, STextInput } from "../atoms";
import {
  calculateNumberFromPercentage,
  calculatePercentage,
} from "../../utils";
import { useSettings, useSpaces } from "../../hooks";

import { AxiosResponse } from "axios";
import { BugTracker } from "../../library";
import SAbsoluteTooltip from "./SAbsoluteTooltip";
import SCheckButton from "../atoms/SCheckButton";
import SLineChart from "./SLineChart";
import SSpacer from "../atoms/SSpacer";

interface IProps {
  space: Space;
  report: any;
  onCheck: (check: Check) => void;
  updateCapacity: (
    id: string,
    capacity: number
  ) => Promise<AxiosResponse<any> | undefined>;
  updateSafeCapacity: (
    id: string,
    safeCapacity: number
  ) => Promise<AxiosResponse<any> | undefined>;
}

const SChartListCard: React.FC<IProps> = ({
  space,
  report,
  onCheck,
  updateCapacity,
  updateSafeCapacity,
}) => {
  const [targetHover, setTargetHover] = useState<boolean>(false);
  const [targetPressIn, setTargetPressIn] = useState<boolean>(false);
  const [safeHover, setSafeHover] = useState<boolean>(false);
  const [safePressIn, setSafePressIn] = useState<boolean>(false);
  const [capacity, setCapacity] = useState<string>();
  const [safe, setSafe] = useState<string>();

  const capacityInputReference = useRef<any>(null);
  const safeInputReference = useRef<any>(null);

  const {
    setSpaceSafeCapacity,
    setSpaceTargetCapacity,
    setSpaceLegalCapacity,
  } = useSpaces();
  const { settings } = useSettings();

  const onTargetHoverIn = () => setTargetHover(true);
  const onTargetHoverOut = () => setTargetHover(false);
  const onTargetPressIn = () => {
    setTargetPressIn(true);
    capacityInputReference.current.focus();
  };
  const onTargetPressOut = () => {
    setTargetPressIn(false);
  };
  const onTargetKeyPress = (e: any) => {
    if (e.key === "Escape" || e.key === "Return") {
      setTargetPressIn(false);
    }
  };

  const onSafeHoverIn = () => setSafeHover(true);
  const onSafeHoverOut = () => setSafeHover(false);
  const onSafePressIn = () => {
    setSafePressIn(true);
    safeInputReference.current.focus();
  };

  const onSafePressOut = () => {
    setSafePressIn(false);
  };
  const onSafeKeyPress = (e: any) => {
    if (e.key === "Escape" || e.key === "Return") {
      setSafePressIn(false);
    }
  };
  const onSafeSubmitEditing = async (value: {
    nativeEvent: {
      text: string;
      eventCount: number;
      target: string;
    };
  }) => {
    // If written in backend then update app state
    const parsed = parseInt(value.nativeEvent.text);
    if (isNaN(parsed)) {
      return 0;
    }
    const capacity = isNaN(parsed)
      ? 0
      : calculateNumberFromPercentage(parsed, space.target_capacity);

    try {
      await updateSafeCapacity(space.id, capacity);
      setSpaceSafeCapacity(space.id, capacity);
      setSafePressIn(false);
    } catch (error) {
      BugTracker.captureException(error);
    }
  };

  const onTargetSubmitEditing = async (value: {
    nativeEvent: {
      text: string;
      eventCount: number;
      target: string;
    };
  }) => {
    // Write Target Capacity

    //If written in backend then update app state
    const parsed = parseInt(value.nativeEvent.text);
    if (isNaN(parsed)) {
      return 0;
    }
    const capacity = isNaN(parsed) ? 0 : parsed;
    try {
      await updateCapacity(space.id, capacity);
      settings?.capacity === DataType.LegalCapacity
        ? setSpaceLegalCapacity(space.id, capacity)
        : setSpaceTargetCapacity(space.id, capacity);
      setTargetPressIn(false);
    } catch (error) {
      BugTracker.captureException(error);
    }
  };

  const onChangeTargetCapacity = (text: string) => {
    if (/^\d+$/.test(text) || text === "") {
      setCapacity(text);
    }
  };

  const onChangeSafeCapacity = (text: string) => {
    setSafe(text.split("%")[0]);
  };

  const renderSafeCapacity = () => {
    return settings?.safe === DataType.SafeCapacityPpl ? (
      <View style={styles.columnNumber}>
        <Text style={styles.numberText}>
          {space?.safe_capacity ? space.safe_capacity : ""}
        </Text>
      </View>
    ) : (
      <Pressable
        style={[
          Styles.columnEditableNumber,
          {
            justifyContent: "flex-start",
          },
        ]}
        onPress={onSafePressIn}
        onMouseEnter={onSafeHoverIn}
        onMouseLeave={onSafeHoverOut}
        onPressIn={onSafePressIn}
        onPressOut={onSafePressOut}
      >
        {(space.safe_capacity || space.safe_capacity === 0) && !safePressIn ? (
          <>
            {(space.safe_capacity || space.safe_capacity === 0) &&
            space.target_capacity ? (
              <SPercentChip
                noColor
                value={space.safe_capacity}
                total={space.target_capacity}
                bg
              />
            ) : (
              <Text style={styles.numberText}>{space.safe_capacity} ppl</Text>
            )}
            <SSpacer
              type={Orientation.Horizontal}
              space={Metrics.paddingSuperSmall}
            />
            <View style={styles.icon}>
              <SIcon
                type={SIconType.Pen}
                size={Metrics.iconExtraSmall}
                color={
                  safeHover && !safePressIn
                    ? Colors.grey300
                    : Colors.transparent
                }
              />
            </View>
          </>
        ) : (
          <View style={{ width: 64 }}>
            <STextInput
              reference={(input) => {
                safeInputReference.current = input;
              }}
              placeholder={
                space.safe_capacity
                  ? `${calculatePercentage(
                      parseInt(space.safe_capacity),
                      space.target_capacity
                    )}`
                  : ""
              }
              value={safe ? safe : ""}
              accessoryIcon={false}
              textStyle={{
                ...Typography.editNumber,
                color: Colors.grey900,
                width: 54,
              }}
              onChangeText={onChangeSafeCapacity}
              onSubmitEditing={onSafeSubmitEditing}
              onKeyPress={onSafeKeyPress}
              onFocus={() => {}}
              onBlur={onSafePressOut}
            />
          </View>
        )}
      </Pressable>
    );
  };

  return (
    <View style={styles.container}>
      <SSpacer type={Orientation.Horizontal} space={Metrics.padding8} />
      <View style={styles.column1}>
        <SCheckButton
          checkType={space.selected ? Check.Checked : Check.Unchecked}
          onCheck={onCheck}
        />
        <View style={styles.nameContainer}>
          <Text style={styles.nameText} ellipsizeMode="tail" numberOfLines={1}>
            {space.name}
          </Text>
          <Text
            style={styles.addressText}
            ellipsizeMode="tail"
            numberOfLines={2}
          >
            {space.address}
          </Text>
        </View>
      </View>
      <SSpacer type={Orientation.Horizontal} space={Metrics.padding24} />
      <Pressable
        style={[styles.columnNumber, { width: Metrics.size108 }]}
        onPress={onTargetPressIn}
        onMouseEnter={onTargetHoverIn}
        onMouseLeave={onTargetHoverOut}
        onPressIn={onTargetPressIn}
        onPressOut={onTargetPressOut}
      >
        {space.target_capacity && !targetPressIn ? (
          <>
            <SSpacer
              type={Orientation.Horizontal}
              space={Metrics.paddingSuperSmall + Metrics.iconExtraSmall}
            />

            <Text style={styles.numberText}>
              {space.target_capacity ? space.target_capacity : "SET"}
            </Text>
            <SSpacer
              type={Orientation.Horizontal}
              space={Metrics.paddingSuperSmall}
            />
            <View style={styles.icon}>
              <SIcon
                type={SIconType.Pen}
                size={Metrics.iconExtraSmall}
                color={
                  targetHover && !targetPressIn
                    ? Colors.grey300
                    : Colors.transparent
                }
              />
            </View>
          </>
        ) : (
          <STextInput
            reference={(input) => {
              capacityInputReference.current = input;
            }}
            placeholder={space.target_capacity ? space.target_capacity : ""}
            value={capacity}
            accessoryIcon={false}
            textStyle={{
              ...Typography.bodyNumber,
              color: Colors.grey900,
              width: Metrics.size86 - 2 * Metrics.padding8,
            }}
            onChangeText={onChangeTargetCapacity}
            onKeyPress={onTargetKeyPress}
            onSubmitEditing={onTargetSubmitEditing}
            onBlur={onTargetPressOut}
            onFocus={() => {}}
          />
          // <SIconLink
          //   type={SIconType.Pen}
          //   title=""
          //   size={Metrics.iconExtraSmall}
          //   onPress={() => {}}
          // />
        )}
      </Pressable>
      <SSpacer type={Orientation.Horizontal} space={Metrics.padding24} />
      {renderSafeCapacity()}
      <SSpacer type={Orientation.Horizontal} space={Metrics.padding24} />
      <View style={styles.chartContainer}>
        <SLineChart
          space={space}
          report={report ? report : []}
          height={Metrics.size96}
          disabled={
            space.target_capacity === undefined ||
            space.target_capacity === null
          }
        />
        {space.target_capacity === undefined ||
        space.target_capacity === null ? null : (
          <SAbsoluteTooltip
            text={"Safe Threshold"}
            style={{
              position: "absolute",
              top: 2.5 + (Metrics.size96 - 10) - 64,
              left: -2.5,
              height: 5,
              width: 5,
            }}
          >
            <View
              style={{
                height: 5,
                width: 5,
                borderRadius: 2.5,
                backgroundColor: Colors.green400,
              }}
            />
          </SAbsoluteTooltip>
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: "row",
    flexGrow: 1,
    marginVertical: 2,
    height: Metrics.size96,
    width: "100%",
    borderRadius: Metrics.borderRadius8,
    backgroundColor: Colors.white,
  },
  chartContainer: {
    flex: 1,
    alignItems: "flex-end",
    paddingLeft: 5,
  },
  column1: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-start",
    width: Metrics.size300,
  },
  columnNumber: {
    flexDirection: "row",
    width: Metrics.size90,
    justifyContent: "center",
    alignItems: "center",
  },
  icon: {
    position: "absolute",
    right: 0,
  },
  nameContainer: {
    marginLeft: Metrics.paddingSmallStandard,
    justifyContent: "center",
    width: Metrics.size300 - Metrics.paddingSmallStandard - Metrics.padding24,
    marginRight: 10,
    flexShrink: 1,
  },
  nameText: {
    ...Typography.bodyTitle,
  },
  addressText: {
    ...Typography.smaller,
    color: Colors.grey500,
    marginTop: 2,
  },
  numberText: {
    ...Typography.bodyNumber,
    color: Colors.grey500,
  },
});

export default SChartListCard;
